import { createRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { EmptyState, Screen, Skeleton, type Tab } from "../../shared/ui/index.js";
import { Stagger, Item } from "../../shared/motion.js";
import { Shell } from "../../app/Shell.js";
import { requireAuth } from "../../app/guards.js";
import { rootRoute } from "../../app/root-route.js";
import { useLeads, type Lead } from "./leads.api.js";
import { LeadCard } from "./LeadCard.js";
import { ConvertLeadDialog } from "./ConvertLeadDialog.js";

export const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  beforeLoad: requireAuth("admin"),
  component: () => (
    <Shell>
      <LeadsScreen />
    </Shell>
  ),
});

const TABS: Tab[] = [
  { id: "all", label: "Toutes" },
  { id: "new", label: "Nouvelles" },
  { id: "open", label: "En cours" },
  { id: "won", label: "Gagnées" },
  { id: "lost", label: "Perdues" },
];

const matches = (lead: Lead, tab: string) =>
  tab === "all"
    ? true
    : tab === "open"
      ? lead.status === "contacted" || lead.status === "quoted"
      : lead.status === tab;

function LeadsScreen() {
  const { data, isLoading } = useLeads();
  const [tab, setTab] = useState("all");
  const [convert, setConvert] = useState<Lead | null>(null);

  const items = data?.items ?? [];
  const stats = useMemo(() => {
    const by = (s: string) => items.filter((i) => i.status === s).length;
    return {
      total: items.length,
      new: by("new"),
      open: by("contacted") + by("quoted"),
      won: by("won"),
    };
  }, [items]);
  const shown = items.filter((l) => matches(l, tab));

  return (
    <Screen title="Demandes de prix" tabs={TABS} activeTab={tab} onTab={setTab}>
      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {[
          ["Total", stats.total],
          ["Nouvelles", stats.new],
          ["En cours", stats.open],
          ["Gagnées", stats.won],
        ].map(([label, n]) => (
          <div key={label} className="rounded-xl border border-stone-200 bg-white px-3 py-2">
            <p className="text-xl font-semibold text-stone-900">{n}</p>
            <p className="text-xs text-stone-400">{label}</p>
          </div>
        ))}
      </div>

      {isLoading ? (
        <Skeleton count={3} className="h-24" />
      ) : shown.length === 0 ? (
        <EmptyState>Aucune demande dans cette catégorie.</EmptyState>
      ) : (
        <Stagger className="flex flex-col gap-2">
          {shown.map((lead) => (
            <Item key={lead.id}>
              <LeadCard lead={lead} onConvert={() => setConvert(lead)} />
            </Item>
          ))}
        </Stagger>
      )}

      {convert && <ConvertLeadDialog lead={convert} onClose={() => setConvert(null)} />}
    </Screen>
  );
}
